import { useState } from "react";
import { Button } from "../design/button";
import { Heading, Subheading } from "../design/heading";
import { Text } from "../design/text";
import { FeatureType } from "../models/FeatureType";
import { useMatter } from "../contexts/MatterContext";
import { MatterService } from "../services/MatterService";

interface UpsellCardBaseProps {
  productKey: FeatureType;
  title: string;
  description: string;
  subheading?: string;
  price: string | number;
  imageSrc: string;
  imageAlt?: string;
}

export const UpsellCardBase: React.FC<UpsellCardBaseProps> = ({
  productKey,
  title,
  description,
  subheading,
  price,
  imageSrc,
  imageAlt,
}) => {
  const { currentMatterId } = useMatter();
  const [isUnlocking, setIsUnlocking] = useState(false);

  const onUnlock = async () => {
    if (!currentMatterId) return;
    setIsUnlocking(true);
    try {
      await MatterService.unlockFeature(currentMatterId, productKey);
      window.location.reload();
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please try again.");
    }
    setIsUnlocking(false);
  };

  return (
    <div className="flex flex-col gap-4 p-6 bg-gray-100 border shadow-sm md:flex-row rounded-xl dark:bg-zinc-900">
      {/* Top image - small screens only */}
      <div className="flex justify-center border rounded-lg sm:hidden">
        <img
          src={imageSrc}
          alt={imageAlt ?? title}
          className="object-cover w-full h-full rounded-lg shadow-md"
        />
      </div>

      {/* Main content */}
      <div className="gap-4 md:w-3/4">
        <Text>{title}</Text>
        <div>
          <Heading>{title}</Heading>
          <Text>{description}</Text>
        </div>
        {subheading && (
          <div>
            <Subheading>{subheading}</Subheading>
            <div className="text-2xl font-bold">${price}</div>
          </div>
        )}
        <div className="flex items-center gap-4 mt-4">
          <Button href={`/product/${productKey}`}>Get started</Button>
          <Text>or</Text>
          <Button onClick={onUnlock} disabled={isUnlocking}>
            {isUnlocking ? "Unlocking..." : "Unlock for Free"}
          </Button>
        </div>
      </div>

      {/* Bottom image - medium screens and up */}
      <div className="justify-center hidden border rounded-lg md:flex md:w-1/4">
        <img
          src={imageSrc}
          alt={imageAlt ?? title}
          className="object-cover w-full h-full rounded-lg shadow-md"
        />
      </div>
    </div>
  );
};
